import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
    DoorClosed,
    DoorOpen,
    Eye,
    History as HistoryIcon,
    MoreVertical,
    Pencil,
    Plug,
    Search,
    CircleMinus,
    Trash2,
    Wallet,
    X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import StatusChip from '../../component/common/StatusChip';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import { MobileCashRegisterList } from '../../component/cashregister/MobileCashRegisters';
import CloseCashDrawer from './components/CloseCashDrawer';
import IntegrationModal from './components/IntegrationModal';
import OpenCashModal from './components/OpenCashModal';
import SangriaModal from './components/SangriaModal';
import CashRegisterForm from './Form';
import { formatBRMoney, parseBRDateToISO, parseBRMoney } from './shared';

// Lista de caixas — DESIGN-SYSTEM.md, mesma estrutura de
// `pages/services/List.js` (`report-toolbar` + `chips-row` + `DefaultTable`).
// Ref: `prototipo-lista-caixas.html`.
//
// Terminal (não sessão): `GET /cashregister` (`cashregisterListResponse`) só
// devolve id/name/status/available_cash — nenhuma coluna de "Operador
// atual" na listagem em lote. Para preencher essa coluna com dado real (não
// fabricado), cada linha busca também `GET /cashregister/{id}/resume` em
// paralelo (`Promise.all`) — endpoint que já devolve `details.operator`
// (quem tem o caixa travado via `cash_register_lock`, populado por
// `/cashregister/{id}/select`) e `details.open_date` (usado para compor
// "Última movimentação").
//
// Correção de bug: o caixa não se conecta com nenhuma maquininha (não existe
// essa funcionalidade real) e não há coluna de "local" no schema — a coluna/
// filtro "Integração" (Conectado/Desconectado) e a exibição de "Local" foram
// removidos. A ação "Integração" do menu de cada linha continua abrindo o
// modal, que agora só mostra o código real (`code_integration`, ver
// `components/IntegrationModal.js`).
export default function List() {
    const navigate = useNavigate();
    const location = useLocation();
    const isMobileShell = useIsMobileShell();
    const { hasPermission } = usePermissions();
    const canView = hasPermission('cashregister.view');
    const canUpdate = hasPermission('cashregister.update');
    const canHistory = hasPermission('cashregister.history');
    const canOpen = hasPermission('cashregister.open');
    const canClose = hasPermission('cashregister.close');
    const canBleed = hasPermission('cashregister.bleed');
    const canDelete = hasPermission('cashregister.delete');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [overrides, setOverrides] = useState({});

    // Filtros — aplicados só ao clicar "Buscar"/"Limpar" (mesma convenção de
    // `prototipo-lista-caixas.html`, cujos 3 campos só filtram via
    // `applyFilters()`).
    const [draftStatus, setDraftStatus] = useState('');
    const [draftSearch, setDraftSearch] = useState('');
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');

    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetCashRegisterId, setSheetCashRegisterId] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [cashRegisterToDelete, setCashRegisterToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [openModalRow, setOpenModalRow] = useState(null);
    const [sangriaModalRow, setSangriaModalRow] = useState(null);
    const [integrationModalRow, setIntegrationModalRow] = useState(null);
    const [closeDrawerRow, setCloseDrawerRow] = useState(null);

    const showError = (responseData) => {
        let text = 'Não foi possível concluir a operação.';
        if (responseData == null || responseData === '') {
            text = 'Não foi possível concluir a operação.';
        } else if (typeof responseData === 'object') {
            text = responseData.message || responseData.error || JSON.stringify(responseData);
        } else {
            text = String(responseData);
        }
        setSeverity('error');
        setMessage(text);
        setOpenSnackbar(true);
    };

    const showSuccess = (text) => {
        setSeverity('success');
        setMessage(text);
        setOpenSnackbar(true);
    };

    const fetchCashRegisters = async () => {
        setLoading(true);
        try {
            const response = await httpClient.get('/cashregister');
            const base = Array.isArray(response.data) ? response.data : [];
            const enriched = await Promise.all(base.map(async (cr) => {
                try {
                    const resumeResponse = await httpClient.get(`/cashregister/${cr.id}/resume`);
                    const resume = resumeResponse.data || {};
                    const details = resume.details || {};
                    return {
                        ...cr,
                        operator: details.operator || null,
                        openDate: details.open_date || '',
                        availableChangeCash: resume.available_cash || cr.available_cash,
                        totalPix: resume.total_pix || '0,00',
                        totalGrossMachine: resume.total_gross_machine || '0,00',
                    };
                } catch (error) {
                    console.error(`Erro ao carregar resumo do caixa ${cr.id}:`, error);
                    return { ...cr, operator: null, openDate: '' };
                }
            }));
            setRows(enriched);
        } catch (error) {
            console.error('Erro ao carregar caixas:', error);
            showError('Não foi possível carregar os caixas.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCashRegisters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Shell mobile: as telas de formulário (página cheia) devolvem o aviso de sucesso por `location.state`.
    useEffect(() => {
        const toast = location.state?.toast;
        if (!toast) return;
        showSuccess(toast);
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const combinedRows = useMemo(() => rows.map((row) => {
        const ov = overrides[row.id] || {};
        const isOpen = row.status === 'open';
        const lastMovement = isOpen
            ? (row.openDate ? `Aberto desde ${row.openDate}` : 'Aberto')
            : (row.openDate ? `Última atualização em ${row.openDate}` : 'Fechado');
        return {
            ...row,
            active: ov.active !== false,
            lastMovementLabel: ov.lastMovementLabel || lastMovement,
        };
    }), [rows, overrides]);

    const filteredRows = useMemo(() => combinedRows.filter((row) => {
        if (status && row.status !== status) return false;
        if (search) {
            const term = search.toLowerCase();
            if (!row.name.toLowerCase().includes(term)) return false;
        }
        return true;
    }), [combinedRows, status, search]);

    const totalOpen = combinedRows.filter((r) => r.status === 'open').length;

    const applyFilters = () => {
        setStatus(draftStatus);
        setSearch(draftSearch.trim());
    };

    const clearFilters = () => {
        setDraftStatus('');
        setDraftSearch('');
        setStatus('');
        setSearch('');
    };

    const mergeOverride = (id, patch) => {
        setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    };

    // ---- drawer de formulário (Novo/Editar) ----
    const closeFormSheet = () => {
        setSheetOpen(false);
        setSheetCashRegisterId(null);
    };

    const handleFormSheetSuccess = async (updated) => {
        const wasEdit = Boolean(sheetCashRegisterId);
        setSheetOpen(false);
        setSheetCashRegisterId(null);
        const { active, ...rest } = updated || {};
        if (wasEdit && updated?.id) {
            setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...rest } : r)));
            mergeOverride(updated.id, { active });
        } else {
            // Criar devolve um `id` que não bate com o real gravado no banco
            // (achado de backend documentado em `Form.js`) — recarrega a
            // lista inteira em vez de inserir otimisticamente uma linha com
            // id inválido.
            await fetchCashRegisters();
        }
        showSuccess(wasEdit ? 'Caixa atualizado com sucesso!' : 'Caixa cadastrado com sucesso!');
    };

    // ---- menu dropdown de ações por linha ----
    const handleMenuClick = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const handleView = (row) => {
        handleMenuClose();
        navigate(`/cashregisters/view/${row.id}`);
    };
    const handleEdit = (row) => {
        handleMenuClose();
        setSheetCashRegisterId(row.id);
        setSheetOpen(true);
    };
    const handleHistory = (row) => {
        handleMenuClose();
        navigate(`/cashregisters/history/${row.id}`);
    };
    const handleOpenCash = (row) => {
        handleMenuClose();
        setOpenModalRow(row);
    };
    const handleCloseCash = (row) => {
        handleMenuClose();
        setCloseDrawerRow(row);
    };
    const handleSangria = (row) => {
        handleMenuClose();
        setSangriaModalRow(row);
    };
    const handleIntegration = (row) => {
        handleMenuClose();
        setIntegrationModalRow(row);
    };
    const handleDeleteClick = (row) => {
        handleMenuClose();
        setCashRegisterToDelete(row);
        setOpenDeleteDialog(true);
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            // `httpClient` nunca rejeita por status HTTP (ver
            // `common/httpclient.js`) — status checado à mão; a mensagem de
            // erro (ex.: 409 com caixa aberto) vem no corpo da resposta.
            const response = await httpClient.delete(`/cashregister/${cashRegisterToDelete.id}`);
            if (response.status === 200 || response.status === 204) {
                showSuccess('Caixa excluído com sucesso!');
                fetchCashRegisters();
                setOpenDeleteDialog(false);
                setCashRegisterToDelete(null);
            } else {
                showError(response.data || 'Não foi possível excluir o caixa.');
            }
        } catch (error) {
            console.error('Erro ao excluir caixa:', error);
            showError(error.response?.data || error.message || 'Não foi possível excluir o caixa.');
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            name: 'Terminal',
            path: 'name',
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{row.name}</Typography>
            ),
        },
        {
            name: 'Status',
            path: 'status',
            render: (row) => <StatusChip domain="cashRegisterStatus" value={row.status === 'open' ? 'open' : 'closed'} />,
        },
        {
            name: 'Operador atual',
            path: 'operator',
            render: (row) => (
                row.operator
                    ? <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{row.operator}</Typography>
                    : <Typography sx={{ color: 'text.disabled' }}>—</Typography>
            ),
        },
        {
            name: 'Última movimentação',
            path: 'lastMovementLabel',
            render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.lastMovementLabel}</Typography>,
        },
        {
            name: 'Ações',
            path: 'actions',
            render: (row) => (
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleMenuClick(e, row); }}
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.disabled',
                        '&:hover': { color: 'primary.main', borderColor: 'primary.main', bgcolor: 'info.bg' },
                    }}
                >
                    <MoreVertical size={16} strokeWidth={2} />
                </IconButton>
            ),
        },
    ];

    // Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), mesmo padrão
    // de `pages/services/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const statusLabel = status === 'open' ? 'Aberto' : status === 'closed' ? 'Fechado' : 'Todos';

    const isOpen = (row) => row.status === 'open';

    // Shell mobile: lista do protótipo `caixas/lista.html` (mesmos dados, filtro e permissões).
    if (isMobileShell) {
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget="Caixas" message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
                <MobileCashRegisterList
                    rows={filteredRows}
                    loading={loading}
                    search={draftSearch}
                    onSearchChange={(value) => { setDraftSearch(value); setSearch(value.trim()); }}
                    status={draftStatus}
                    onStatusChange={setDraftStatus}
                    onApplyFilters={applyFilters}
                    onClearFilters={clearFilters}
                    canCreate={hasPermission('cashregister.create')}
                    onOpen={canView ? handleView : undefined}
                    onNew={() => navigate('/cashregisters/new')}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget="Caixas" message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head */}
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                            <Wallet size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Caixas
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Terminais de caixa cadastrados — abertura, fechamento, sangria e código de integração.
                            </Typography>
                        </Box>
                    </Box>
                    {hasPermission('cashregister.create') && (
                        <NewItemButton label="Novo caixa" onClick={() => { setSheetCashRegisterId(null); setSheetOpen(true); }} />
                    )}
                </Box>

                {/* report-toolbar */}
                <FilterToolbar
                    plain
                    activeCount={draftStatus ? 1 : 0}
                    onClear={clearFilters}
                    onApply={applyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 240px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome do terminal…"
                                value={draftSearch}
                                onChange={(e) => setDraftSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search size={15} strokeWidth={2} /></InputAdornment>,
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={clearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={applyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <TbLabel>Status</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={draftStatus}
                                    displayEmpty
                                    onChange={(e) => setDraftStatus(e.target.value)}
                                    renderValue={() => (draftStatus === 'open' ? 'Aberto' : draftStatus === 'closed' ? 'Fechado' : 'Todos')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="open">Aberto</MenuItem>
                                    <MenuItem value="closed">Fechado</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                </FilterToolbar>

                {/* chips-row */}
                <FilterChips>
                    {status && (
                        <Chip label={`Status: ${statusLabel}`} size="small" onDelete={() => { setStatus(''); setDraftStatus(''); }} deleteIcon={<X size={12} strokeWidth={2.5} />} sx={{ bgcolor: 'info.bg', color: 'primary.main' }} />
                    )}
                    {search && (
                        <Chip label={`Busca: "${search}"`} size="small" onDelete={() => { setSearch(''); setDraftSearch(''); }} deleteIcon={<X size={12} strokeWidth={2.5} />} sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                    )}
                    <Chip label={`${filteredRows.length} caixa${filteredRows.length === 1 ? '' : 's'} encontrado${filteredRows.length === 1 ? '' : 's'}`} size="small" sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                    <Chip label={`Abertos agora: ${totalOpen}`} size="small" sx={{ bgcolor: 'success.bg', color: 'success.main' }} />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={filteredRows}
                    loading={loading}
                    onRowClick={canView ? (row) => handleView(row) : undefined}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="caixas"
                />
            </Box>

            {/* menu dropdown flutuante de ações por linha */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 1, minWidth: 220, p: '6px', border: '1px solid', borderColor: 'divider', boxShadow: (t) => t.customShadows?.lg } }}
                MenuListProps={{ sx: { display: 'flex', flexDirection: 'column', gap: '1px', py: 0 } }}
            >
                {selectedRow && [
                    canView && (
                        <MenuItem key="view" onClick={() => handleView(selectedRow)} sx={menuItemSx}>
                            <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}><Eye size={15} strokeWidth={2} /></ListItemIcon>
                            <ListItemText primary="Ver" primaryTypographyProps={typoProps} />
                        </MenuItem>
                    ),
                    canUpdate && (
                        <MenuItem key="edit" onClick={() => handleEdit(selectedRow)} sx={menuItemSx}>
                            <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}><Pencil size={15} strokeWidth={2} /></ListItemIcon>
                            <ListItemText primary="Editar" primaryTypographyProps={typoProps} />
                        </MenuItem>
                    ),
                    canHistory && (
                        <MenuItem key="history" onClick={() => handleHistory(selectedRow)} sx={menuItemSx}>
                            <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}><HistoryIcon size={15} strokeWidth={2} /></ListItemIcon>
                            <ListItemText primary="Histórico" primaryTypographyProps={typoProps} />
                        </MenuItem>
                    ),
                    (canView || canUpdate || canHistory) && (canOpen || canClose || canBleed || canView || canDelete) && <Divider key="sep1" sx={{ my: '4px' }} />,
                    isOpen(selectedRow) ? canClose && (
                        <MenuItem key="close" onClick={() => handleCloseCash(selectedRow)} sx={menuItemSx}>
                            <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}><DoorClosed size={15} strokeWidth={2} /></ListItemIcon>
                            <ListItemText primary="Fechar caixa" primaryTypographyProps={typoProps} />
                        </MenuItem>
                    ) : canOpen && (
                        <MenuItem key="open" onClick={() => handleOpenCash(selectedRow)} sx={menuItemSx}>
                            <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}><DoorOpen size={15} strokeWidth={2} /></ListItemIcon>
                            <ListItemText primary="Abrir caixa" primaryTypographyProps={typoProps} />
                        </MenuItem>
                    ),
                    canBleed && <Tooltip key="sangria" title={isOpen(selectedRow) ? '' : 'Abra o caixa para registrar uma sangria'} placement="left">
                        <span>
                            <MenuItem disabled={!isOpen(selectedRow)} onClick={() => handleSangria(selectedRow)} sx={menuItemSx}>
                                <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}><CircleMinus size={15} strokeWidth={2} /></ListItemIcon>
                                <ListItemText primary="Sangria" primaryTypographyProps={typoProps} />
                            </MenuItem>
                        </span>
                    </Tooltip>,
 canView && (
                        <MenuItem key="integration" onClick={() => handleIntegration(selectedRow)} sx={menuItemSx}>
                            <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}><Plug size={15} strokeWidth={2} /></ListItemIcon>
                            <ListItemText primary="Integração" primaryTypographyProps={typoProps} />
                        </MenuItem>
                    ),
                    canDelete && <Divider key="sep2" sx={{ my: '4px' }} />,
                    canDelete && <Tooltip key="delete" title={isOpen(selectedRow) ? 'Feche o caixa antes de excluir' : ''} placement="left">
                        <span>
                            <MenuItem
                                disabled={isOpen(selectedRow)}
                                onClick={() => handleDeleteClick(selectedRow)}
                                sx={{ ...menuItemSx, color: 'error.main', '&:hover': { bgcolor: 'error.bg' } }}
                            >
                                <ListItemIcon sx={{ minWidth: 0, color: 'error.main' }}><Trash2 size={15} strokeWidth={2} /></ListItemIcon>
                                <ListItemText primary="Excluir" primaryTypographyProps={typoProps} />
                            </MenuItem>
                        </span>
                    </Tooltip>,
                ]}
            </Menu>

            {/* drawer: Novo/Editar caixa */}
            <Drawer
                anchor="right"
                open={sheetOpen}
                onClose={closeFormSheet}
                PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}
            >
                {sheetOpen && (
                    <CashRegisterForm
                        embedded
                        cashRegisterId={sheetCashRegisterId}
                        initialOverrides={sheetCashRegisterId ? overrides[sheetCashRegisterId] : null}
                        onClose={closeFormSheet}
                        onSuccess={handleFormSheetSuccess}
                        onError={showError}
                    />
                )}
            </Drawer>

            {/* modal: excluir */}
            <DialogForm
                open={openDeleteDialog}
                iconType="warning"
                onClose={() => { setOpenDeleteDialog(false); setCashRegisterToDelete(null); }}
                onSubmit={handleDelete}
                title="Excluir Caixa"
                content={`Tem certeza que deseja excluir o caixa "${cashRegisterToDelete?.name || ''}"?`}
                secondaryContent="Esta ação não pode ser desfeita."
                confirmButtonText="Confirmar Exclusão"
                cancelButtonText="Cancelar"
                loading={deleting}
                loadingText="Excluindo..."
            />

            {/* modal: abrir caixa */}
            <OpenCashModal
                open={Boolean(openModalRow)}
                cashRegister={openModalRow}
                onClose={() => setOpenModalRow(null)}
                onError={showError}
                onSuccess={({ amount }) => {
                    setRows((prev) => prev.map((r) => (r.id === openModalRow.id ? { ...r, status: 'open', operator: null, openDate: 'Agora mesmo' } : r)));
                    mergeOverride(openModalRow.id, { lastMovementLabel: `Caixa aberto com valor inicial de R$ ${amount || '0,00'} · agora mesmo` });
                    showSuccess(`Caixa "${openModalRow.name}" aberto com sucesso.`);
                    setOpenModalRow(null);
                }}
            />

            {/* modal: sangria */}
            <SangriaModal
                open={Boolean(sangriaModalRow)}
                cashRegister={sangriaModalRow}
                onClose={() => setSangriaModalRow(null)}
                onError={showError}
                onSuccess={({ amount }) => {
                    mergeOverride(sangriaModalRow.id, { lastMovementLabel: `Sangria de R$ ${formatBRMoney(parseBRMoney(amount))} · agora mesmo` });
                    showSuccess(`Sangria de R$ ${formatBRMoney(parseBRMoney(amount))} registrada em "${sangriaModalRow.name}".`);
                    setSangriaModalRow(null);
                }}
            />

            {/* modal: integração */}
            <IntegrationModal
                open={Boolean(integrationModalRow)}
                cashRegister={integrationModalRow}
                onClose={() => setIntegrationModalRow(null)}
            />

            {/* drawer: fechar caixa */}
            <CloseCashDrawer
                open={Boolean(closeDrawerRow)}
                cashRegister={closeDrawerRow}
                sessionStart={closeDrawerRow ? { label: closeDrawerRow.openDate || '—', dateIso: parseBRDateToISO(closeDrawerRow.openDate) } : null}
                expected={closeDrawerRow ? {
                    dinheiro: parseBRMoney(closeDrawerRow.availableChangeCash || closeDrawerRow.available_cash),
                    pix: parseBRMoney(closeDrawerRow.totalPix),
                    cartao: parseBRMoney(closeDrawerRow.totalGrossMachine),
                } : null}
                onClose={() => setCloseDrawerRow(null)}
                onError={showError}
                onSuccess={({ totalCounted }) => {
                    setRows((prev) => prev.map((r) => (r.id === closeDrawerRow.id ? { ...r, status: 'closed', operator: null } : r)));
                    mergeOverride(closeDrawerRow.id, { lastMovementLabel: `Fechado agora mesmo (total contado: R$ ${formatBRMoney(totalCounted)})` });
                    showSuccess(`Caixa "${closeDrawerRow.name}" fechado com sucesso.`);
                    setCloseDrawerRow(null);
                }}
            />
        </PageScaffold>
    );
}

const menuItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    py: '9px',
    px: '10px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: 'text.secondary',
    '&:hover': { backgroundColor: 'background.subtle', color: 'text.primary' },
};

const typoProps = { fontSize: '13px', fontWeight: 600 };
